import dayjs from 'dayjs';

import { EMainProductCodeType } from '@webtrader/core/api/config/company';
import { ISymbolDetail } from '@webtrader/core/api/market/symbols/detail';
import { EOrderIndicate, IOrder } from '@webtrader/core/api/order';
import { IPendingOrder } from '@webtrader/core/api/order/pending';
import {
  BUY_SELL_TYPE_LABEL_TEXT_FN_MAP,
  EBuySellType,
  TRADE_FILL_POLICY_TEXT_FN_MAP
} from '@webtrader/core/constants/order';
import { getSecondsSinceEpoch } from '@webtrader/core/dayjs';
import { getOrderBuySellType, mapOrderTypeToOrderTypeLabel } from '@webtrader/core/format/order';
import { kSeparatorForNumberOrDecimals } from '@webtrader/core/format/price';
import { convertToDisplayUTC } from '@webtrader/core/hooks/timezone/useDisplayUTCTime';
import { TMixpanelProperties } from '@webtrader/core/mix-panel/types';
import {
  EMixpanelPositionModuleTab,
  EMixpanelProperties,
  EMixpanelTrackFunction,
  ETradePreference
} from '@webtrader/core/services/analysis/constants';
import userHelper from '@webtrader/core/services/analysis/helpers/UserHelper';
import {
  IMixpanelConfirmEditOrderClickProps,
  IMixpanelTrackOrderClickProps,
  ITrackOrderProps
} from '@webtrader/core/services/analysis/order/type';
import { chartService } from '@webtrader/core/services/chart';
import { companyService } from '@webtrader/core/services/company';
import { IOrderFormData } from '@webtrader/core/services/form/order/service';
import { orderService } from '@webtrader/core/services/order';
import { userPreferenceService } from '@webtrader/core/services/userPreference';

import {
  ICreateEditLimitOrderPropterties,
  ICreateEditMarketOrderPropterties,
  ICreatePlaceOrderClickPropertiesProps,
  ICreateTrackedOrderPropertiesProps,
  IPlaceOrderDetailsButtonClickProperties
} from './type';

class PlaceOrderHelper {
  public createFullChartProperties = (): TMixpanelProperties => {
    const { fullscreen } = chartService.context || {};
    return {
      [EMixpanelProperties.IS_FULL_CHART]: fullscreen
    };
  };

  public createPlaceOrderClickProperties = ({
    symbol,
    direction,
    orderType
  }: ICreatePlaceOrderClickPropertiesProps): TMixpanelProperties => {
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      [EMixpanelProperties.SYMBOL]: symbol,
      [EMixpanelProperties.DIRECTION]: direction,
      [EMixpanelProperties.ORDER_TYPE]: orderType,
      ...fullChartProperties,
      ...userAccountProperties
    };
  };

  public createTradePreferenceProperties = (): TMixpanelProperties => {
    const { isUserOCTEnable } = userPreferenceService.context || {};
    let tradePreference = ETradePreference.TWO_CLICK_TRADING;
    if (isUserOCTEnable) {
      tradePreference = ETradePreference.ONE_CLICK_TRADING;
    }
    return {
      [EMixpanelProperties.TRADE_PREFERENCE]: tradePreference
    };
  };

  public createBuySellDirectionProperties = (buySellType: EBuySellType): TMixpanelProperties => {
    return {
      [EMixpanelProperties.DIRECTION]: BUY_SELL_TYPE_LABEL_TEXT_FN_MAP[buySellType]()
    };
  };

  // eslint-disable-next-line complexity
  private createOrderProperties = (
    values: Partial<IOrderFormData>,
    symbolDetails: ISymbolDetail,
    displayTime: string
  ): TMixpanelProperties => {
    const { symbol, contractSize, ask, bid, currency, marginInitial, swapLong, swapShort, stopsLevel } =
      symbolDetails || {};
    const { volume, fillPolicy, stopLoss, takeProfit, price, priceTrigger, tradeExpiry, expiration, orderTypeLabel } =
      values || {};

    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? TRADE_FILL_POLICY_TEXT_FN_MAP[fillPolicy]() : '-',
      [EMixpanelProperties.INDICATE]: EOrderIndicate.PRICE,
      [EMixpanelProperties.LOT_SIZE]: volume ?? '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ?? '-',
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ?? '-',
      [EMixpanelProperties.ORDER_TYPE]: orderTypeLabel,
      [EMixpanelProperties.ASK]: ask ?? '-',
      [EMixpanelProperties.BID]: bid ?? '-',
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency,
      [EMixpanelProperties.CONTRACT_SIZE]: contractSize,
      [EMixpanelProperties.PRICE]: price ? Number(price) : '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ?? '-',
      [EMixpanelProperties.TRADE_EXPIRY]: tradeExpiry ?? '-',
      [EMixpanelProperties.EXPIRATION]: expiration
        ? convertToDisplayUTC(dayjs(expiration).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.DISPLAY_TIME]: displayTime ?? '-',
      [EMixpanelProperties.INITIAL_MARGIN]: marginInitial ?? '-',
      [EMixpanelProperties.SWAP_LONG]: swapLong ?? '-',
      [EMixpanelProperties.SWAP_SHORT]: swapShort ?? '-',
      [EMixpanelProperties.MIN_STOP_DISTANCE]: stopsLevel ?? '-'
    };
  };

  public createPlaceOrderSubmittedProperties = ({
    values,
    symbolDetails,
    buySellType,
    displayTime
  }: ITrackOrderProps) => {
    const buySellTypeProperties = this.createBuySellDirectionProperties(buySellType);
    const orderDetailsProperties = this.createOrderProperties(values, symbolDetails, displayTime);
    const orderUserProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();
    const fullChartProperties = this.createFullChartProperties();
    return {
      ...fullChartProperties,
      ...orderDetailsProperties,
      ...buySellTypeProperties,
      ...orderUserProperties,
      ...tradePreference
    };
  };

  public createPlaceOrderDetailsButtonClickProperties = ({
    symbol,
    modalType
  }: IPlaceOrderDetailsButtonClickProperties) => {
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();
    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.MODAL_TYPE]: modalType ?? '-',
      ...fullChartProperties,
      ...userAccountProperties,
      ...tradePreference
    };
  };

  // eslint-disable-next-line complexity
  private createEditMarketOrderProperties = ({ values, order, livePrice }: ICreateEditMarketOrderPropterties) => {
    const { stopLoss, takeProfit } = values || {};
    const {
      symbol,
      currency,
      openTime,
      orderType,
      units,
      openPrice,
      currentPrice,
      swap,
      commission,
      fillPolicy,
      lotSize: maxLotSize
    } = order;

    const convertedOpenTime = getSecondsSinceEpoch(
      companyService.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );
    return {
      [EMixpanelProperties.DIRECTION]: BUY_SELL_TYPE_LABEL_TEXT_FN_MAP[getOrderBuySellType(orderType)](),
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: maxLotSize ? kSeparatorForNumberOrDecimals(maxLotSize) : '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? TRADE_FILL_POLICY_TEXT_FN_MAP[fillPolicy]() : '-',
      [EMixpanelProperties.OPEN_PRICE]: openPrice ? kSeparatorForNumberOrDecimals(openPrice) : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? kSeparatorForNumberOrDecimals(livePrice) : currentPrice ?? '-',
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ?? '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ?? '-',
      [EMixpanelProperties.SWAP]: swap ? kSeparatorForNumberOrDecimals(swap) : '-',
      [EMixpanelProperties.COMMISSION]: commission ? kSeparatorForNumberOrDecimals(commission) : '-',
      [EMixpanelProperties.UNITS]: units ? kSeparatorForNumberOrDecimals(units) : '-'
    };
  };

  // eslint-disable-next-line complexity
  public createEditLimitOrderProperties = ({ values, order, livePrice }: ICreateEditLimitOrderPropterties) => {
    const {
      price, // Entry Price
      priceTrigger, // Stop Limit Price / Pending Price
      stopLoss,
      takeProfit,
      tradeExpiry,
      expiration
    } = values;

    const { lotSize, fillPolicy, symbol, openTime, orderType, units, currentPrice, filledLotSize } = order;

    const isMT5 = companyService.getIntegration() === EMainProductCodeType.METATRADER5;

    const displayLotSize = () => {
      const filled = Number.isFinite(lotSize) ? kSeparatorForNumberOrDecimals(lotSize) : '-';
      if (isMT5) {
        const unfill = Number.isFinite(filledLotSize) ? kSeparatorForNumberOrDecimals(filledLotSize) : '-';
        return `${filled} / ${unfill}`;
      }
      return filled;
    };

    const convertedOpenTime = getSecondsSinceEpoch(
      companyService.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );

    const convertedExpiration = getSecondsSinceEpoch(
      companyService.convertToDayjsWithOffset({ input: expiration, keepTime: true })
    );
    return {
      [EMixpanelProperties.DIRECTION]: BUY_SELL_TYPE_LABEL_TEXT_FN_MAP[getOrderBuySellType(orderType)](),
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: displayLotSize() ?? '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? TRADE_FILL_POLICY_TEXT_FN_MAP[fillPolicy]() : '-',
      [EMixpanelProperties.EXPIRATION]: expiration
        ? convertToDisplayUTC(dayjs(convertedExpiration).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.TRADE_EXPIRY]: tradeExpiry ? tradeExpiry : '-',
      [EMixpanelProperties.PRICE]: price ?? '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ?? '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? kSeparatorForNumberOrDecimals(livePrice) : currentPrice ?? 0,
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ?? '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ?? '-',
      [EMixpanelProperties.UNITS]: units ? kSeparatorForNumberOrDecimals(units) : '-'
    };
  };

  public createConfirmEditOrderClickProperties = ({
    positionModuleTab,
    order,
    values,
    livePrice
  }: IMixpanelConfirmEditOrderClickProps) => {
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();

    let orderProperties = {};
    if (positionModuleTab === EMixpanelPositionModuleTab.OPEN_POSITIONS) {
      orderProperties = this.createEditMarketOrderProperties({
        values,
        order: order as IOrder,
        livePrice
      });
    } else if (positionModuleTab === EMixpanelPositionModuleTab.PENDING_ORDERS) {
      orderProperties = this.createEditLimitOrderProperties({
        values,
        order: order as IPendingOrder,
        livePrice
      });
    }
    return {
      [EMixpanelProperties.POSITION_MODULE_TAB]: positionModuleTab ?? '-',
      ...fullChartProperties,
      ...userAccountProperties,
      ...tradePreference,
      ...orderProperties
    };
  };

  private createTrackedOrderProperties = ({
    trackOrderParams,
    livePrice,
    priceTrigger
  }: ICreateTrackedOrderPropertiesProps) => {
    const { symbol, orderId, orderType, stopLoss, takeProfit, entryPrice } = trackOrderParams;
    const profitTrackingOrders = orderService.getProfitTrackingOrders();
    const currentOrderWithLiveProfit = profitTrackingOrders.find((o) => o.orderId === orderId);
    const profitLoss = currentOrderWithLiveProfit?.profit;
    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.DIRECTION]: BUY_SELL_TYPE_LABEL_TEXT_FN_MAP[getOrderBuySellType(orderType)](),
      [EMixpanelProperties.STOP_LOSS]: stopLoss ? kSeparatorForNumberOrDecimals(stopLoss) : '-',
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ? kSeparatorForNumberOrDecimals(takeProfit) : '-',
      [EMixpanelProperties.ENTRY_PRICE]: entryPrice ? kSeparatorForNumberOrDecimals(entryPrice) : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? kSeparatorForNumberOrDecimals(livePrice) : '-',
      [EMixpanelProperties.PROFIT_LOSS]: profitLoss ? kSeparatorForNumberOrDecimals(profitLoss) : '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ? kSeparatorForNumberOrDecimals(priceTrigger) : '-'
    };
  };

  public createTracKOrderClickProperties = ({
    trackOrderParams,
    livePrice,
    positionModuleTab,
    isTracking,
    priceTrigger
  }: IMixpanelTrackOrderClickProps) => {
    const trackedOrder = this.createTrackedOrderProperties({ trackOrderParams, livePrice, priceTrigger });
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();
    return {
      [EMixpanelProperties.POSITION_MODULE_TAB]: positionModuleTab ?? '-',
      [EMixpanelProperties.FUNCTION]: isTracking ? EMixpanelTrackFunction.TRACK : EMixpanelTrackFunction.UNTRACK,
      ...fullChartProperties,
      ...userAccountProperties,
      ...tradePreference,
      ...trackedOrder
    };
  };
}

export const placeOrderHelper = new PlaceOrderHelper();
export default placeOrderHelper;
